import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Anchor } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../config/api";

// BACKEND_URL imported from config/api
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const navigate = useNavigate();

  const handleLogoClick = () => {
    setLogoClicks(prev => prev + 1);
    if (logoClicks + 1 >= 3) {
      toast.success("🔓 Secret admin portal unlocked!");
      setTimeout(() => navigate('/platform-admin'), 500);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter both username and password");
      return;
    }

    setLoading(true);
    try {
      // Check if email format (could be super admin or org admin)
      if (username.includes('@')) {
        // Try super admin first
        try {
          const response = await axios.post(`${API}/auth/super-admin-login`, {
            email: username,
            password
          });
          
          if (response.data.success && response.data.userType === "super_admin") {
            localStorage.setItem("userType", "super_admin");
            localStorage.setItem("userData", JSON.stringify({ email: username, name: response.data.name }));
            toast.success("Welcome, Super Admin!");
            navigate("/super-admin");
            return;
          }
        } catch (superAdminError) {
          // Not super admin, try organization admin
        }
        
        // Try organization admin login
        try {
          const response = await axios.post(`${API}/auth/org-login`, {
            email: username,
            password
          });
          
          if (response.data.success && response.data.userType === "org_admin") {
            // Store organization data
            localStorage.setItem("userType", "admin");
            localStorage.setItem("organizationId", response.data.organizationId);
            localStorage.setItem("organizationName", response.data.organizationName);
            localStorage.setItem("dbConnectionString", response.data.dbConnectionString);
            localStorage.setItem("userData", JSON.stringify({
              email: response.data.email,
              name: response.data.name,
              organizationName: response.data.organizationName
            }));
            toast.success(`Welcome, ${response.data.name}!`);
            navigate("/admin");
            return;
          }
        } catch (orgError) {
          // Show specific error message if account is pending
          if (orgError.response && orgError.response.status === 403) {
            toast.error(orgError.response.data.detail || "Account access denied");
            setLoading(false);
            return;
          }
          // Not org admin either, continue to other login checks
        }
      }

      // Check if admin login (existing single-tenant admin)
      if (username === "admin" && password === "admin123") {
        localStorage.setItem("userType", "admin");
        localStorage.setItem("userData", JSON.stringify({ username: "admin", name: "Administrator" }));
        toast.success("Welcome, Administrator!");
        navigate("/admin");
        return;
      }

      // Try team_member login
      try {
        const response = await axios.post(`${API}/teamMember-login`, {
          username,
          password
        });
        
        // Store team_member data
        localStorage.setItem("userType", "teamMember");
        localStorage.setItem("teamMemberData", JSON.stringify(response.data));
        toast.success(`Welcome back, ${response.data.name}!`);
        navigate("/teamMember-view");
      } catch (teamMemberError) {
        // Neither admin nor valid team_member
        toast.error("Invalid username or password");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div 
              onClick={handleLogoClick}
              className="p-4 rounded-xl shadow-lg cursor-pointer hover:scale-105 transition-transform bg-blue-600"
            >
              <Anchor className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">
            Booking<span className="text-blue-600">Flows</span>
          </CardTitle>
          <p className="text-gray-600 mt-2">Sign in to your account</p>
        </CardHeader>
        <CardContent>
          {/* Google OAuth Button */}
          <div className="space-y-4 mb-6">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border border-gray-200 hover:bg-gray-50"
              onClick={() => {
                toast.info('Social sign-in available for approved accounts');
              }}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or continue with credentials</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                disabled={loading}
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
            <Button
              data-testid="login-button"
              type="submit"
              className="w-full py-6 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
            <div className="text-center text-sm text-gray-600 mt-4 space-y-2">
              <p>Admin or TeamMember Login</p>
              <div className="flex items-center justify-between pt-2 border-t">
                <Button
                  type="button"
                  variant="link"
                  className="text-blue-600 hover:underline p-0 h-auto"
                  onClick={() => navigate('/')}
                >
                  ← Back to Home
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="text-blue-600 hover:underline p-0 h-auto"
                  onClick={() => navigate('/signup')}
                >
                  Create Organization →
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;